import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface PortalPassenger {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string | null;
  loyalty_points: number;
  loyalty_tier: string | null;
  loyalty_number: string | null;
}

export interface PortalRoute {
  id: number;
  name: string;
  code: string;
  origin: string;
  destination: string;
  distance_km: string;
  estimated_duration_minutes: number;
  timetables: Array<{
    id: number;
    name: string;
    code: string;
    departure_time: string;
    arrival_time: string;
  }>;
}

export interface PortalTicket {
  id: number;
  ticket_number: string;
  booking_reference: string;
  passenger_name: string;
  status: string;
  payment_status: string;
  departure_date: string;
  total_price: string;
  currency_code: string;
  seat_number: string | null;
  route?: { id: number; name: string; code: string } | null;
}

export interface PortalComplaint {
  id: number;
  complaint_number: string;
  complaint_type: string;
  subject: string;
  description: string;
  status: string;
  severity: string;
  priority: string;
  created_at: string;
}

const portalClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

portalClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('portalToken');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

portalClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('portalToken');
      window.dispatchEvent(new CustomEvent('portal:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export const portalApi = {
  async register(payload: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    password: string;
    password_confirmation: string;
  }) {
    const { data } = await portalClient.post('/portal/auth/register', payload);
    return data.data as { passenger: PortalPassenger; token: string };
  },

  async login(email: string, password: string) {
    const { data } = await portalClient.post('/portal/auth/login', { email, password });
    return data.data as { passenger: PortalPassenger; token: string };
  },

  async logout() {
    await portalClient.post('/portal/auth/logout');
  },

  async me() {
    const { data } = await portalClient.get('/portal/auth/me');
    return data.data as { passenger: PortalPassenger };
  },

  async searchRoutes(params: { origin?: string; destination?: string; date?: string }) {
    const { data } = await portalClient.get('/portal/routes', { params });
    return data.data as { routes: PortalRoute[]; date: string };
  },

  async book(payload: {
    route_id: number;
    timetable_id?: number;
    travel_date: string;
    seats_requested: number;
    passenger_name?: string;
    passenger_phone?: string;
    passenger_email?: string;
    price?: number;
    notes?: string;
  }) {
    const { data } = await portalClient.post('/portal/bookings', payload);
    return data.data as {
      reservation: Record<string, unknown>;
      ticket: PortalTicket;
    };
  },

  async tickets(page = 1) {
    const { data } = await portalClient.get('/portal/tickets', { params: { page, per_page: 15 } });
    return data.data as { current_page: number; last_page: number; data: PortalTicket[] };
  },

  async ticket(id: number) {
    const { data } = await portalClient.get(`/portal/tickets/${id}`);
    return data.data as PortalTicket;
  },

  async ticketPdf(id: number) {
    const { data } = await portalClient.get(`/portal/tickets/${id}/pdf`);
    const payload = data.data as { filename: string; content_base64: string; content_type: string };
    const bytes = atob(payload.content_base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type: payload.content_type });
  },

  async lookup(reference: string) {
    const { data } = await portalClient.get('/portal/bookings/lookup', {
      params: { reference },
    });
    return data.data as { ticket: PortalTicket };
  },

  async complaints() {
    const { data } = await portalClient.get('/portal/complaints', { params: { per_page: 50 } });
    return data.data as { data: PortalComplaint[] };
  },

  async storeComplaint(payload: {
    complaint_type: string;
    subject: string;
    description: string;
    severity?: string;
    priority?: string;
    related_ticket_id?: number;
  }) {
    const { data } = await portalClient.post('/portal/complaints', payload);
    return data.data as PortalComplaint;
  },

  async loyalty() {
    const { data } = await portalClient.get('/portal/loyalty');
    return data.data as {
      loyalty_number: string | null;
      loyalty_points: number;
      loyalty_tier: string | null;
      total_trips: number;
      transactions: Array<{ id: number; points: number; type: string; reason: string; created_at: string }>;
    };
  },
};

export function downloadPdf(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default portalClient;
